// FormBuilder — three-pane drag-free editor for a single form template.
// Left: sections list. Center: fields preview. Right: selected field properties.
import { useCallback, useEffect, useRef, useState, Fragment } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft, Plus, Trash2, ChevronUp, ChevronDown, Save, CheckCircle, X, Loader2,
    Type, AlignLeft, Hash, Calendar, List, Grid, ToggleLeft, Camera, Pen, Info, CheckSquare,
    UserCheck, User, Eye, EyeOff, Table2, ClipboardPaste,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FormPreviewBody } from "@/components/forms/FormPreviewDialog";

const FIELD_TYPE_CONFIG = {
    text:          { icon: Type,        label: "Text",            color: "#6F6A64" },
    textarea:      { icon: AlignLeft,   label: "Paragraph",       color: "#6F6A64" },
    number:        { icon: Hash,        label: "Number",          color: "#2A5DB0" },
    date:          { icon: Calendar,    label: "Date",            color: "#6B4E9E" },
    dropdown:      { icon: List,        label: "Dropdown",        color: "#C68A1C" },
    multi_select:  { icon: Grid,        label: "Multi-select",    color: "#C68A1C" },
    checkbox:      { icon: CheckSquare, label: "Checkbox",        color: "#3F7D3A" },
    yes_no:        { icon: ToggleLeft,  label: "Yes / No / N/A",  color: "#2A5DB0" },
    internal_name: { icon: UserCheck,   label: "Internal name (PR staff)", color: "#2A5DB0" },
    external_name: { icon: User,        label: "External name (free text)", color: "#6F6A64" },
    photo:         { icon: Camera,      label: "Photo upload",    color: "var(--brand-accent, #C75A1B)" },
    signature:     { icon: Pen,         label: "Signature",       color: "#9F4212" },
    instruction:   { icon: Info,        label: "Instruction",     color: "#9A968F" },
    check_grid:    { icon: Table2,      label: "Check grid",      color: "#2A5DB0" },
};

const CHECK_COLUMN_TYPES = [
    { value: "yes_no", label: "Yes / No", values: ["Yes", "No"] },
    { value: "open_closed", label: "Open / Closed", values: ["Open", "Closed"] },
    { value: "number", label: "Number", values: [] },
    { value: "text", label: "Text", values: [] },
    { value: "select", label: "Select (custom options)", values: [] },
];
const genId = (prefix) => `${prefix}${Date.now()}-${Math.floor(Math.random() * 10000)}`;

const NEEDS_OPTIONS = new Set(["dropdown", "multi_select"]);
const INTERNAL_EDIT_ROLES = new Set(["director", "administrator", "consultant"]);

const newField = (type = "text") => ({
    id: `f${Date.now()}-${Math.floor(Math.random() * 1000)}`,
    label: "", field_type: type, required: false,
    placeholder: "", content: "", help_text: "", options: [],
    ...(type === "check_grid" ? { grid: { id_columns: [], check_columns: [], rows: [], allow_add_rows: false } } : {}),
});
const newSection = () => ({
    id: `s${Date.now()}`, title: "New section", order: 0, fields: [],
});

export default function FormBuilder() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const isInternal = user && INTERNAL_EDIT_ROLES.has(user.role);
    const ownClientId = user?.client_id || (user?.client_ids || [])[0] || null;

    const [template, setTemplate] = useState(null);
    const isSiteMgrProposal = user?.role === "site_manager" && template?.client_id === ownClientId
        && ["pending_approval", "rejected", "draft"].includes(template?.status) && user?.can_manage_forms !== false;
    const canEdit = isInternal || (user?.role === "client_admin" && template?.client_id === ownClientId && user?.can_manage_forms !== false) || isSiteMgrProposal;
    const [sections, setSectionsState] = useState([]);
    const sectionsRef = useRef([]);
    const setSections = useCallback((updater) => {
        setSectionsState((prev) => {
            const next = typeof updater === "function" ? updater(prev) : updater;
            sectionsRef.current = next;
            return next;
        });
    }, []);
    const [activeSectionIdx, setActiveSectionIdx] = useState(0);
    const [activeFieldId, setActiveFieldId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [metaOpen, setMetaOpen] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [meta, setMeta] = useState({ name: "", description: "", category: "inspection", requires_signature: true, completed_by: "pr", pr_approval_required: false, report_category: "" });
    const [scopeClientName, setScopeClientName] = useState(null);
    const [buildings, setBuildings] = useState([]);

    useEffect(() => {
        if (!template?.client_id) { setBuildings([]); return; }
        api.get("/buildings", { params: { client_id: template.client_id } })
            .then(({ data }) => setBuildings(data || [])).catch(() => setBuildings([]));
    }, [template?.client_id]);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/forms/templates/${id}`);
                setTemplate(data);
                setSections(data.sections || []);
                sectionsRef.current = data.sections || [];
                setMeta({
                    name: data.name, description: data.description || "",
                    category: data.category, requires_signature: data.requires_signature,
                    completed_by: data.completed_by || "pr",
                    pr_approval_required: !!data.pr_approval_required,
                    report_category: data.report_category || "",
                });
                if (data.client_id) {
                    api.get(`/clients/${data.client_id}`).then((r) => setScopeClientName(r.data?.name)).catch(() => {});
                }
            } catch (e) {
                toast.error("Could not load template");
                navigate("/app/templates");
            }
            setLoading(false);
        })();
    }, [id, navigate]);

    const save = useCallback(async (sectionsToSave, silent = false) => {
        const latest = sectionsToSave || sectionsRef.current;
        setSaving(true);
        try {
            await api.patch(`/forms/templates/${id}`, {
                sections: latest.map((s, i) => ({ ...s, order: i })),
            });
            if (!silent) toast.success("Saved");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
        }
        setSaving(false);
    }, [id]);

    const saveMeta = async () => {
        try {
            const { data } = await api.patch(`/forms/templates/${id}`, meta);
            setTemplate(data);
            toast.success("Settings saved");
            setMetaOpen(false);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
        }
    };

    const publish = async () => {
        setPublishing(true);
        try {
            await save(sectionsRef.current, true);
            const { data } = await api.post(`/forms/templates/${id}/publish`);
            setTemplate(data);
            toast.success("Template published");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Publish failed");
        }
        setPublishing(false);
    };

    // Prompt 2 — a site manager resubmits a rejected proposal back into the approval queue.
    const resubmit = async () => {
        setPublishing(true);
        try {
            await save(sectionsRef.current, true);
            const { data } = await api.patch(`/forms/templates/${id}`, { status: "pending_approval" });
            setTemplate(data);
            toast.success("Resubmitted for approval");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not resubmit");
        }
        setPublishing(false);
    };

    // Section ops
    const addSection = () => {
        const s = newSection();
        const next = [...sections, s];
        setSections(next);
        setActiveSectionIdx(next.length - 1);
        setActiveFieldId(null);
    };
    const removeSection = (idx) => {
        if (sections.length === 1) return toast.error("Keep at least one section");
        const next = sections.filter((_, i) => i !== idx);
        setSections(next);
        setActiveSectionIdx(Math.min(idx, next.length - 1));
        setActiveFieldId(null);
    };
    const moveSection = (idx, dir) => {
        const next = [...sections];
        const j = idx + dir;
        if (j < 0 || j >= next.length) return;
        [next[idx], next[j]] = [next[j], next[idx]];
        setSections(next);
        setActiveSectionIdx(j);
    };
    const updateSectionTitle = (idx, title) =>
        setSections((s) => s.map((sec, i) => (i === idx ? { ...sec, title } : sec)));
    const updateSectionField = (idx, key, val) =>
        setSections((s) => s.map((sec, i) => (i === idx ? { ...sec, [key]: val } : sec)));

    // Field ops
    const activeSection = sections[activeSectionIdx];
    const addField = (ftype) => {
        if (!activeSection) return;
        const f = newField(ftype);
        setSections((s) => s.map((sec, i) =>
            i === activeSectionIdx ? { ...sec, fields: [...(sec.fields || []), f] } : sec));
        setActiveFieldId(f.id);
    };
    const updateField = (fieldId, patch) => {
        setSections((s) => s.map((sec, i) => {
            if (i !== activeSectionIdx) return sec;
            return { ...sec, fields: (sec.fields || []).map((f) => (f.id === fieldId ? { ...f, ...patch } : f)) };
        }));
    };
    const deleteField = (fieldId) => {
        setSections((s) => s.map((sec, i) =>
            i !== activeSectionIdx ? sec : { ...sec, fields: (sec.fields || []).filter((f) => f.id !== fieldId) }));
        setActiveFieldId(null);
    };
    const moveField = (fieldId, dir) => {
        setSections((s) => s.map((sec, i) => {
            if (i !== activeSectionIdx) return sec;
            const fields = [...(sec.fields || [])];
            const idx = fields.findIndex((f) => f.id === fieldId);
            const j = idx + dir;
            if (j < 0 || j >= fields.length) return sec;
            [fields[idx], fields[j]] = [fields[j], fields[idx]];
            return { ...sec, fields };
        }));
    };

    const activeField = activeSection?.fields?.find((f) => f.id === activeFieldId) || null;
    const allFieldsFlat = sections.flatMap((s) => (s.fields || []).map((f) => ({ ...f, sectionTitle: s.title })));
    // Running section number skips hidden sections entirely, so it always
    // matches what the inspector actually sees (and what lands in the PDF).
    const visibleSectionNumber = new Map();
    { let n = 0; sections.forEach((s) => { if (s.enabled !== false) visibleSectionNumber.set(s.id, ++n); }); }

    if (loading) {
        return <div className="p-10 text-center text-[#9A968F]"><Loader2 className="inline animate-spin mr-2" size={16} />Loading template…</div>;
    }
    if (!template) return <div className="p-10 text-center text-[#9A968F]">Template not found.</div>;

    const isPublished = template.status === "published";
    const readonly = !canEdit;

    return (
        <div data-testid="form-builder" className="flex flex-col -mx-6 -my-6" style={{ minHeight: "calc(100vh - 64px)" }}>
            {/* Top bar */}
            <div className="flex items-center gap-3 px-5 py-3 border-b border-[#E5E0DA] bg-white">
                <button data-testid="builder-back" onClick={() => navigate("/app/templates")}
                    className="p-1.5 rounded hover:bg-[#F6F4F0] text-[#6F6A64]">
                    <ArrowLeft size={16} />
                </button>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-[#1F1F1F] truncate">{template.name}</span>
                        <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold uppercase"
                            style={{
                                background: isPublished ? "#E8F1E6" : "#FCF1DC",
                                color: isPublished ? "#3F7D3A" : "#C68A1C",
                            }}>{template.status}</span>
                        <span className="text-[11px] text-[#9A968F]">v{template.version}</span>
                        <span className="text-[11px] text-[#9A968F]">· {template.code}</span>
                        <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold"
                            style={{ background: "#EFEAE3", color: "#6F6A64" }}>
                            {template.client_id ? (scopeClientName || "Client template") : "Global master"}
                        </span>
                        <span data-testid="builder-completed-by" className="text-[11px] px-2 py-0.5 rounded-full font-semibold"
                            title="Who is allowed to run this form — not the same as who owns the completed report"
                            style={{
                                background: template.completed_by === "client" ? "#E6F0F9" : "#FBEFE3",
                                color: template.completed_by === "client" ? "#2A5DB0" : "#9F4212",
                            }}>
                            Completed by {template.completed_by === "client" ? "client" : "PR"}
                        </span>
                        {template.pr_approval_required && (
                            <span data-testid="builder-pr-approval-pill" className="text-[11px] px-2 py-0.5 rounded-full font-semibold"
                                title="Priority Risk must approve this report after it is submitted — even when the client completes it"
                                style={{ background: "#FBEFE3", color: "#9F4212" }}>
                                PR approval required
                            </span>
                        )}
                    </div>
                </div>
                <button data-testid="builder-preview-toggle" onClick={() => setPreviewOpen((v) => !v)}
                    aria-pressed={previewOpen}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-semibold rounded-lg border transition-colors"
                    style={previewOpen
                        ? { background: "var(--brand-accent, #C75A1B)", borderColor: "var(--brand-accent, #C75A1B)", color: "#fff" }
                        : { borderColor: "#E5E0DA", color: "#1F1F1F" }}>
                    {previewOpen ? <EyeOff size={13} /> : <Eye size={13} />} {previewOpen ? "Hide preview" : "Preview"}
                </button>
                {!readonly && (
                    <>
                        <button data-testid="builder-settings" onClick={() => setMetaOpen(true)}
                            className="px-3 py-1.5 text-[13px] font-semibold rounded-lg border border-[#E5E0DA] text-[#1F1F1F] hover:bg-[#F6F4F0]">
                            Settings
                        </button>
                        <button data-testid="builder-save" onClick={() => save()} disabled={saving}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-semibold rounded-lg border border-[#E5E0DA] text-[#1F1F1F] hover:bg-[#F6F4F0] disabled:opacity-60">
                            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save
                        </button>
                        {!isPublished && !isSiteMgrProposal && (
                            <button data-testid="builder-publish" onClick={publish} disabled={publishing}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-semibold rounded-lg text-white disabled:opacity-60"
                                style={{ background: "#3F7D3A" }}>
                                {publishing ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle size={13} />} Publish
                            </button>
                        )}
                        {isSiteMgrProposal && template.status === "rejected" && (
                            <button data-testid="builder-resubmit" onClick={resubmit} disabled={publishing}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-semibold rounded-lg text-white disabled:opacity-60"
                                style={{ background: "var(--brand-accent, #C75A1B)" }}>
                                {publishing ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle size={13} />} Resubmit for approval
                            </button>
                        )}
                        {isSiteMgrProposal && template.status === "pending_approval" && (
                            <span data-testid="builder-awaiting-approval" className="text-[12px] font-semibold px-3 py-1.5 rounded-lg" style={{ background: "#FCF1DC", color: "#C68A1C" }}>
                                Awaiting Priority Risk approval
                            </span>
                        )}
                    </>
                )}
            </div>

            {template.status === "rejected" && template.rejection_note && (
                <div data-testid="builder-rejection-note" className="px-5 py-2.5 bg-[#FBEBE8] border-b border-[#F1C9C2] text-[13px] text-[#9B2C1E] flex items-start gap-2">
                    <X size={15} className="mt-0.5 shrink-0" />
                    <span><b>Rejected by {template.reviewed_by_name || "Priority Risk"}:</b> {template.rejection_note}{isSiteMgrProposal ? " — make the changes and click Resubmit for approval." : ""}</span>
                </div>
            )}

            <div className="flex flex-1 overflow-hidden">
                {/* Sections */}
                <div className="w-56 border-r border-[#E5E0DA] bg-[#F5F3F0] flex flex-col">
                    <div className="px-3 py-2.5 border-b border-[#E5E0DA] flex items-center justify-between">
                        <span className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64]">Sections</span>
                        {!readonly && (
                            <button data-testid="builder-add-section" onClick={addSection}
                                className="p-1 rounded hover:bg-[#F0EDE8] text-[#6F6A64]"><Plus size={14} /></button>
                        )}
                    </div>
                    <div className="flex-1 overflow-y-auto py-2 px-2 space-y-1">
                        {sections.map((sec, idx) => (
                            <div key={sec.id} data-testid={`builder-section-${idx}`}
                                className={`group flex items-center gap-1.5 px-2 py-2 rounded-lg cursor-pointer ${activeSectionIdx === idx
                                        ? "bg-white border border-[#E5E0DA] shadow-sm"
                                        : "hover:bg-white/70"
                                    }`}
                                onClick={() => { setActiveSectionIdx(idx); setActiveFieldId(null); }}>
                                <span className="w-5 h-5 rounded text-[10px] font-bold flex-shrink-0 grid place-items-center text-white"
                                    style={{ background: sec.enabled === false ? "#C0BCB4" : (activeSectionIdx === idx ? "var(--brand-accent, #C75A1B)" : "#C0BCB4") }}>
                                    {sec.enabled === false ? <EyeOff size={10} /> : visibleSectionNumber.get(sec.id)}
                                </span>
                                <span className={`text-[12px] flex-1 truncate font-semibold ${sec.enabled === false ? "text-[#9A968F] italic" : "text-[#1F1F1F]"}`}>{sec.title}</span>
                                {!readonly && (
                                    <div className="flex gap-0 opacity-0 group-hover:opacity-100">
                                        <button onClick={(e) => { e.stopPropagation(); updateSectionField(idx, "enabled", sec.enabled === false ? true : false); }}
                                            data-testid={`builder-section-toggle-${idx}`} title={sec.enabled === false ? "Show section (include in form + export)" : "Hide section (excludes it from the form and export, keeps its content)"}
                                            className="p-0.5 hover:bg-[#F0EDE8] rounded text-[#6F6A64]">
                                            {sec.enabled === false ? <Eye size={11} /> : <EyeOff size={11} />}
                                        </button>
                                        <button onClick={(e) => { e.stopPropagation(); moveSection(idx, -1); }}
                                            disabled={idx === 0} className="p-0.5 hover:bg-[#F0EDE8] rounded disabled:opacity-30"><ChevronUp size={11} /></button>
                                        <button onClick={(e) => { e.stopPropagation(); moveSection(idx, 1); }}
                                            disabled={idx === sections.length - 1} className="p-0.5 hover:bg-[#F0EDE8] rounded disabled:opacity-30"><ChevronDown size={11} /></button>
                                        <button onClick={(e) => { e.stopPropagation(); removeSection(idx); }}
                                            className="p-0.5 hover:bg-red-50 rounded text-[#9A968F] hover:text-red-600"><X size={11} /></button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Fields canvas */}
                <div className="flex-1 overflow-y-auto bg-[#F5F3F0]/40">
                    {activeSection ? (
                        <div className="p-6 max-w-2xl mx-auto">
                            {activeSection.enabled === false && (
                                <div data-testid="builder-section-hidden-banner" className="mb-4 flex items-center gap-2 px-3 py-2 rounded-lg text-[12.5px] font-semibold" style={{ background: "#F6F4F0", color: "#6F6A64" }}>
                                    <EyeOff size={13} /> Hidden — this section won't appear on the form or in exported reports. Its fields and any existing answers are kept.
                                </div>
                            )}
                            <div className="mb-4">
                                <input data-testid="builder-section-title"
                                    value={activeSection.title}
                                    disabled={readonly}
                                    onChange={(e) => updateSectionTitle(activeSectionIdx, e.target.value)}
                                    className="text-lg font-bold text-[#1F1F1F] bg-transparent border-b-2 border-transparent focus:border-[var(--brand-accent,_#C75A1B)] focus:outline-none w-full pb-1" />
                                <p className="text-[11px] text-[#9A968F] mt-0.5">
                                    {(activeSection.fields || []).length} field{(activeSection.fields || []).length !== 1 ? "s" : ""}
                                </p>
                                <div className="mt-2">
                                    <ShowIfEditor
                                        testPrefix="builder-section-showif"
                                        showIf={activeSection.show_if}
                                        onChange={(v) => updateSectionField(activeSectionIdx, "show_if", v)}
                                        availableFields={allFieldsFlat}
                                        readonly={readonly} />
                                </div>
                            </div>

                            <div className="space-y-2 mb-4">
                                {(activeSection.fields || []).length === 0 && (
                                    <div className="py-10 text-center border-2 border-dashed border-[#E5E0DA] rounded-xl text-[12px] text-[#9A968F]">
                                        No fields yet — add one below
                                    </div>
                                )}
                                {(activeSection.fields || []).map((f, idx) => (
                                    <FieldRow key={f.id} field={f}
                                        isSelected={activeFieldId === f.id}
                                        onSelect={() => setActiveFieldId(f.id)}
                                        onMoveUp={() => moveField(f.id, -1)}
                                        onMoveDown={() => moveField(f.id, 1)}
                                        isFirst={idx === 0}
                                        isLast={idx === (activeSection.fields || []).length - 1}
                                        readonly={readonly}
                                    />
                                ))}
                            </div>

                            {!readonly && (
                                <div className="border border-[#E5E0DA] bg-white rounded-xl p-3">
                                    <p className="text-[11px] font-bold text-[#6F6A64] uppercase tracking-wider mb-2">Add field</p>
                                    <div className="grid grid-cols-3 gap-1.5">
                                        {Object.entries(FIELD_TYPE_CONFIG).map(([type, cfg]) => {
                                            const Icon = cfg.icon;
                                            return (
                                                <button key={type} data-testid={`builder-add-field-${type}`}
                                                    onClick={() => addField(type)}
                                                    className="flex items-center gap-1.5 p-2 rounded-lg border border-[#E5E0DA] hover:border-[var(--brand-accent,_#C75A1B)] hover:bg-[#FBEFE5] text-left transition-colors">
                                                    <Icon size={12} style={{ color: cfg.color }} />
                                                    <span className="text-[11px] text-[#1F1F1F] font-semibold">{cfg.label}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="flex items-center justify-center h-full text-[#9A968F] text-sm">Add a section to start.</div>
                    )}
                </div>

                {/* Field editor */}
                <div className="w-72 border-l border-[#E5E0DA] bg-white flex-shrink-0 flex flex-col">
                    <div className="px-4 py-2.5 border-b border-[#E5E0DA]">
                        <span className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64]">Field properties</span>
                    </div>
                    <div className="flex-1 overflow-y-auto">
                        <FieldEditor field={activeField} readonly={readonly}
                            onChange={(patch) => activeField && updateField(activeField.id, patch)}
                            onDelete={() => activeField && deleteField(activeField.id)}
                            allFields={allFieldsFlat} buildings={buildings} clientId={template.client_id} />
                    </div>
                </div>

                {/* Live preview — updates as sections/fields change, no save required */}
                {previewOpen && (
                    <div data-testid="builder-preview-panel" className="w-[420px] border-l border-[#E5E0DA] bg-[#FAF9F7] flex-shrink-0 flex flex-col">
                        <div className="px-4 py-2.5 border-b border-[#E5E0DA] bg-white flex items-center justify-between">
                            <span className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64]">Live preview</span>
                            <span className="text-[10.5px] text-[#9A968F]">Updates as you edit</span>
                        </div>
                        <div className="flex-1 overflow-y-auto p-4">
                            <FormPreviewBody template={{ ...template, sections }} testIdPrefix="builder-preview" />
                        </div>
                        <div className="px-4 py-2 border-t border-[#E5E0DA] bg-white">
                            <span className="text-[10.5px] text-[#9A968F]">Read-only — reflects unsaved changes too.</span>
                        </div>
                    </div>
                )}
            </div>

            {metaOpen && (
                <MetaDialog meta={meta} setMeta={setMeta} onClose={() => setMetaOpen(false)} onSave={saveMeta} isInternal={isInternal} />
            )}
        </div>
    );
}

function FieldRow({ field, isSelected, onSelect, onMoveUp, onMoveDown, isFirst, isLast, readonly }) {
    const cfg = FIELD_TYPE_CONFIG[field.field_type] || FIELD_TYPE_CONFIG.text;
    const Icon = cfg.icon;
    return (
        <div onClick={onSelect} data-testid={`builder-field-${field.id}`}
            className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-all ${isSelected
                    ? "border-[var(--brand-accent,_#C75A1B)] bg-[#FBEFE5]/40 shadow-sm"
                    : "border-[#E5E0DA] bg-white hover:border-[#C0BCB4]"
                }`}>
            <div className="p-1.5 rounded flex-shrink-0" style={{ background: `${cfg.color}22` }}>
                <Icon size={13} style={{ color: cfg.color }} />
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-[13px] font-semibold text-[#1F1F1F] truncate">
                    {field.label || <span className="text-[#9A968F] italic">Untitled field</span>}
                </p>
                <p className="text-[11px] text-[#9A968F]">
                    {cfg.label}{field.required && <span className="text-red-600 ml-1">*</span>}
                </p>
            </div>
            {!readonly && (
                <div className="flex gap-0.5 flex-shrink-0">
                    <button onClick={(e) => { e.stopPropagation(); onMoveUp(); }} disabled={isFirst}
                        className="p-0.5 hover:bg-[#F0EDE8] rounded disabled:opacity-30"><ChevronUp size={13} /></button>
                    <button onClick={(e) => { e.stopPropagation(); onMoveDown(); }} disabled={isLast}
                        className="p-0.5 hover:bg-[#F0EDE8] rounded disabled:opacity-30"><ChevronDown size={13} /></button>
                </div>
            )}
        </div>
    );
}

function FieldEditor({ field, onChange, onDelete, readonly, allFields = [], buildings = [], clientId }) {
    const [gridOpen, setGridOpen] = useState(false);
    if (!field) {
        return (
            <div className="flex flex-col items-center justify-center h-full text-center p-6">
                <p className="text-[12px] text-[#9A968F]">Select a field to edit its properties.</p>
            </div>
        );
    }
    const cfg = FIELD_TYPE_CONFIG[field.field_type] || FIELD_TYPE_CONFIG.text;
    const set = (k, v) => onChange({ [k]: v });

    const addOption = () => set("options", [...(field.options || []), ""]);
    const updOpt = (i, v) => {
        const arr = [...(field.options || [])]; arr[i] = v; set("options", arr);
    };
    const rmOpt = (i) => set("options", (field.options || []).filter((_, j) => j !== i));

    return (
        <div className="p-4 space-y-4">
            <div className="flex items-center gap-2 pb-3 border-b border-[#F0EDE8]">
                <div className="p-1.5 rounded" style={{ background: `${cfg.color}22` }}>
                    <cfg.icon size={13} style={{ color: cfg.color }} />
                </div>
                <span className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64]">{cfg.label}</span>
                {!readonly && (
                    <button data-testid="builder-field-delete" onClick={onDelete}
                        className="ml-auto p-1 rounded hover:bg-red-50 text-[#9A968F] hover:text-red-600">
                        <Trash2 size={13} />
                    </button>
                )}
            </div>

            <Group label="Label *">
                <input data-testid="builder-field-label"
                    data-field-id={field.id}
                    value={field.label} disabled={readonly}
                    onChange={(e) => set("label", e.target.value)}
                    onBlur={(e) => set("label", e.target.value)} className={inputCls} />
            </Group>

            {!["instruction", "photo", "signature", "check_grid"].includes(field.field_type) && (
                <Group label="Placeholder">
                    <input data-testid="builder-field-placeholder" value={field.placeholder || ""} disabled={readonly}
                        onChange={(e) => set("placeholder", e.target.value)} className={inputCls} />
                </Group>
            )}

            {field.field_type === "instruction" && (
                <Group label="Instruction text">
                    <textarea rows={4} value={field.content || ""} disabled={readonly}
                        onChange={(e) => set("content", e.target.value)} className={inputCls + " resize-none"} />
                </Group>
            )}

            {field.field_type === "instruction" && (
                <label className="flex items-start gap-2 text-sm text-[#1F1F1F]">
                    <input type="checkbox" data-testid="builder-field-internal-only" className="mt-0.5"
                        checked={!!field.internal_only} disabled={readonly}
                        onChange={(e) => set("internal_only", e.target.checked)} />
                    <span>
                        Internal only
                        <span className="block text-[11px] text-[#9A968F] mt-0.5">Priority Risk staff only — never shown on the client portal, in the PDF or in an emailed report.</span>
                    </span>
                </label>
            )}

            {field.field_type === "check_grid" && (
                <div className="border border-[#E5E0DA] rounded-lg p-3 bg-[#FAF9F7] space-y-2">
                    <p className="text-[12px] text-[#1F1F1F]">
                        <b>{(field.grid?.id_columns || []).length}</b> reference column{(field.grid?.id_columns || []).length !== 1 ? "s" : ""} ·{" "}
                        <b>{(field.grid?.check_columns || []).length}</b> check column{(field.grid?.check_columns || []).length !== 1 ? "s" : ""} ·{" "}
                        <b>{(field.grid?.rows || []).length}</b> row{(field.grid?.rows || []).length !== 1 ? "s" : ""}
                    </p>
                    <button data-testid="builder-edit-grid-btn" onClick={() => setGridOpen(true)}
                        className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[12.5px] font-semibold text-white"
                        style={{ background: "var(--brand-accent, #C75A1B)" }}>
                        <Table2 size={13} /> {readonly ? "View grid…" : "Edit grid…"}
                    </button>
                </div>
            )}

            {NEEDS_OPTIONS.has(field.field_type) && (
                <div>
                    <div className="flex items-center justify-between mb-1">
                        <label className="block text-[11px] font-bold uppercase tracking-wider text-[#6F6A64]">Options</label>
                        {!readonly && (
                            <button data-testid="builder-field-add-option" onClick={addOption}
                                className="text-[11px] text-[var(--brand-accent,_#C75A1B)] font-semibold hover:underline flex items-center gap-0.5">
                                <Plus size={11} />Add
                            </button>
                        )}
                    </div>
                    <div className="space-y-1.5">
                        {(field.options || []).map((opt, i) => (
                            <div key={i} className="flex items-center gap-1">
                                <input value={opt} disabled={readonly}
                                    onChange={(e) => updOpt(i, e.target.value)}
                                    className={inputCls + " flex-1 h-8"} />
                                {!readonly && (
                                    <button onClick={() => rmOpt(i)}
                                        className="p-1 rounded hover:bg-red-50 text-[#9A968F] hover:text-red-600">
                                        <X size={12} />
                                    </button>
                                )}
                            </div>
                        ))}
                        {(field.options || []).length === 0 && (
                            <p className="text-[11px] text-[#9A968F] italic">No options yet</p>
                        )}
                    </div>
                </div>
            )}

            {field.field_type === "dropdown" && clientId && (
                <div className="border border-[#E5E0DA] rounded-lg p-3 bg-[#FAF9F7] space-y-2">
                    <label className="flex items-center gap-2 text-[12px] font-semibold text-[#1F1F1F]">
                        <input type="checkbox" data-testid="builder-dropdown-rotation-toggle" disabled={readonly}
                            checked={!!field.dropdown_config}
                            onChange={(e) => set("dropdown_config", e.target.checked ? { building_map: {} } : null)} />
                        Map options to buildings (site rotation)
                    </label>
                    <p className="text-[11px] text-[#9A968F]">Each run pre-selects &amp; locks the next building in rotation for the report's site. Priority Risk staff can unlock it to override.</p>
                    {field.dropdown_config && (
                        <div className="space-y-1.5">
                            {(field.options || []).map((opt, i) => (
                                <div key={i} className="flex items-center gap-1.5">
                                    <span className="text-[11.5px] text-[#6F6A64] flex-1 truncate">{opt || `Option ${i + 1}`}</span>
                                    <select data-testid={`builder-dropdown-building-${i}`} disabled={readonly} className={inputCls + " h-8 w-40"}
                                        value={field.dropdown_config.building_map?.[opt] || ""}
                                        onChange={(e) => set("dropdown_config", {
                                            ...field.dropdown_config,
                                            building_map: { ...field.dropdown_config.building_map, [opt]: e.target.value || undefined },
                                        })}>
                                        <option value="">— No building —</option>
                                        {buildings.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                                    </select>
                                </div>
                            ))}
                            {(field.options || []).length === 0 && <p className="text-[11px] text-[#9A968F] italic">Add dropdown options first.</p>}
                        </div>
                    )}
                </div>
            )}

            <Group label="Help text">
                <input value={field.help_text || ""} disabled={readonly}
                    onChange={(e) => set("help_text", e.target.value)}
                    placeholder="Optional hint shown under the field" className={inputCls} />
            </Group>

            {!["instruction", "photo", "signature", "check_grid"].includes(field.field_type) && (
                <Group label="Weekly Site Report role" helper="Tags this answer so the Weekly Site Report can pull it through automatically (e.g. Quality Assurance Inspection's result/ITP stage).">
                    <select data-testid="builder-field-wsr-role" value={field.wsr_role || ""} disabled={readonly}
                        onChange={(e) => set("wsr_role", e.target.value || null)} className={inputCls}>
                        <option value="">Not tagged</option>
                        <option value="result">Result (Pass / Fail / Partial)</option>
                        <option value="itp_stage">ITP stage / hold point</option>
                    </select>
                </Group>
            )}

            {!["instruction", "check_grid"].includes(field.field_type) && (
                <label className="flex items-center gap-2 pt-2 border-t border-[#F0EDE8] text-sm text-[#1F1F1F]">
                    <input type="checkbox" data-testid="builder-field-required"
                        checked={!!field.required} disabled={readonly}
                        onChange={(e) => set("required", e.target.checked)} />
                    Required
                </label>
            )}

            <ShowIfEditor testPrefix={`builder-field-showif-${field.id}`}
                showIf={field.show_if} onChange={(v) => set("show_if", v)}
                availableFields={allFields.filter((f) => f.id !== field.id)} readonly={readonly} />

            {gridOpen && (
                <CheckGridEditorDialog field={field} onChange={onChange} readonly={readonly} onClose={() => setGridOpen(false)}
                    allFields={allFields.filter((f) => f.id !== field.id)} />
            )}
        </div>
    );
}

function CheckGridEditorDialog({ field, onChange, readonly, onClose, allFields = [] }) {
    const grid = field.grid || { id_columns: [], check_columns: [], rows: [], allow_add_rows: false };
    const [tab, setTab] = useState("columns");
    const [pasteOpen, setPasteOpen] = useState(false);
    const [pasteText, setPasteText] = useState("");
    const [showIfRowId, setShowIfRowId] = useState(null);
    const setGrid = (patch) => onChange({ grid: { ...grid, ...patch } });

    const idCols = grid.id_columns || [];
    const chkCols = grid.check_columns || [];
    const rows = grid.rows || [];

    const addIdCol = () => setGrid({ id_columns: [...idCols, { id: genId("idc"), label: "" }] });
    const updIdCol = (i, patch) => setGrid({ id_columns: idCols.map((c, j) => (j === i ? { ...c, ...patch } : c)) });
    const rmIdCol = (i) => setGrid({ id_columns: idCols.filter((_, j) => j !== i) });

    const addChkCol = () => setGrid({ check_columns: [...chkCols, { id: genId("cc"), label: "", type: "yes_no", options: [], required: false, fault_values: [], na_allowed: true }] });
    const updChkCol = (i, patch) => setGrid({ check_columns: chkCols.map((c, j) => (j === i ? { ...c, ...patch } : c)) });
    const rmChkCol = (i) => setGrid({ check_columns: chkCols.filter((_, j) => j !== i) });

    const addRow = () => setGrid({ rows: [...rows, { id: genId("r"), id_values: {}, na_columns: [] }] });
    const updRow = (i, patch) => setGrid({ rows: rows.map((r, j) => (j === i ? { ...r, ...patch } : r)) });
    const rmRow = (i) => {
        if (!window.confirm("Permanently retire this row from the master template? It will no longer appear in any NEW inspection created from this template, for any building/week. Already-started drafts and completed reports are not affected. This cannot be undone.")) return;
        setGrid({ rows: rows.filter((_, j) => j !== i) });
    };
    const toggleRowNa = (i, colId) => {
        const r = rows[i];
        const set = new Set(r.na_columns || []);
        set.has(colId) ? set.delete(colId) : set.add(colId);
        updRow(i, { na_columns: Array.from(set) });
    };

    // "Paste rows" — tab-separated reference data only; auto-skips a header line
    // that matches the id-column labels, truncates extra cells with a note, and
    // shows a preview before any row is actually created.
    const parsedPaste = (() => {
        if (!pasteText.trim()) return { rows: [], skippedHeader: false, truncated: 0 };
        let lines = pasteText.split(/\r?\n/).filter((l) => l.trim().length > 0);
        let skippedHeader = false;
        if (lines.length) {
            const firstCells = lines[0].split("\t").map((c) => c.trim().toLowerCase());
            const labelSet = idCols.map((c) => (c.label || "").trim().toLowerCase());
            if (labelSet.length && firstCells.length && firstCells.every((c, i) => !labelSet[i] || c === labelSet[i])) {
                lines = lines.slice(1);
                skippedHeader = true;
            }
        }
        let truncated = 0;
        const parsedRows = lines.map((line) => {
            const cells = line.split("\t").map((c) => c.trim());
            if (cells.length > idCols.length) truncated++;
            const id_values = {};
            idCols.forEach((c, i) => { id_values[c.id] = cells[i] || ""; });
            return { id: genId("r"), id_values, na_columns: [] };
        });
        return { rows: parsedRows, skippedHeader, truncated };
    })();

    const confirmPaste = () => {
        setGrid({ rows: [...rows, ...parsedPaste.rows] });
        setPasteOpen(false);
        setPasteText("");
    };

    return (
        <div className="fixed inset-0 z-[60] bg-black/40 grid place-items-center p-4" data-testid="check-grid-editor-dialog">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
                <div className="px-5 py-4 border-b border-[#E5E0DA] flex items-center justify-between">
                    <h3 className="text-base font-bold text-[#1F1F1F] flex items-center gap-2"><Table2 size={16} /> Check grid — {field.label || "Untitled"}</h3>
                    <button onClick={onClose} data-testid="check-grid-dialog-close" className="p-1 text-[#9A968F] hover:text-[#1F1F1F]"><X size={18} /></button>
                </div>
                <div className="px-5 pt-3 flex items-center gap-1 border-b border-[#F0EDE8]">
                    {["columns", "rows"].map((t) => (
                        <button key={t} data-testid={`check-grid-tab-${t}`} onClick={() => setTab(t)}
                            className={`px-3 py-2 text-[12.5px] font-semibold border-b-2 -mb-px ${tab === t ? "border-[var(--brand-accent,_#C75A1B)] text-[#1F1F1F]" : "border-transparent text-[#9A968F]"}`}>
                            {t === "columns" ? "Columns" : `Rows (${rows.length})`}
                        </button>
                    ))}
                    <label className="ml-auto flex items-center gap-1.5 text-[12px] text-[#6F6A64] pb-2">
                        <input type="checkbox" data-testid="check-grid-allow-add-rows" checked={!!grid.allow_add_rows} disabled={readonly}
                            onChange={(e) => setGrid({ allow_add_rows: e.target.checked })} />
                        Let the inspector add extra rows
                    </label>
                </div>

                <div className="flex-1 overflow-y-auto p-5 space-y-5">
                    {tab === "columns" && (
                        <>
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64]">Reference columns (read-only — e.g. Door / Apt / Description)</p>
                                    {!readonly && <button data-testid="check-grid-add-idcol" onClick={addIdCol} className="text-[11px] text-[var(--brand-accent,_#C75A1B)] font-semibold hover:underline flex items-center gap-0.5"><Plus size={11} />Add column</button>}
                                </div>
                                <div className="space-y-1.5">
                                    {idCols.map((c, i) => (
                                        <div key={c.id} className="flex items-center gap-1.5">
                                            <input data-testid={`check-grid-idcol-label-${i}`} value={c.label} disabled={readonly} placeholder="Column label"
                                                onChange={(e) => updIdCol(i, { label: e.target.value })} className={inputCls + " flex-1 h-8"} />
                                            {!readonly && <button onClick={() => rmIdCol(i)} className="p-1 rounded hover:bg-red-50 text-[#9A968F] hover:text-red-600"><X size={12} /></button>}
                                        </div>
                                    ))}
                                    {idCols.length === 0 && <p className="text-[11px] text-[#9A968F] italic">No reference columns yet</p>}
                                </div>
                            </div>
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64]">Check columns (what the inspector answers)</p>
                                    {!readonly && <button data-testid="check-grid-add-chkcol" onClick={addChkCol} className="text-[11px] text-[var(--brand-accent,_#C75A1B)] font-semibold hover:underline flex items-center gap-0.5"><Plus size={11} />Add column</button>}
                                </div>
                                <div className="space-y-2.5">
                                    {chkCols.map((c, i) => {
                                        const typeDef = CHECK_COLUMN_TYPES.find((t) => t.value === c.type) || CHECK_COLUMN_TYPES[0];
                                        const faultChoices = c.type === "select" ? (c.options || []) : typeDef.values;
                                        return (
                                            <div key={c.id} className="border border-[#E5E0DA] rounded-lg p-2.5 space-y-2">
                                                <div className="flex items-center gap-1.5">
                                                    <input data-testid={`check-grid-chkcol-label-${i}`} value={c.label} disabled={readonly} placeholder="Column label"
                                                        onChange={(e) => updChkCol(i, { label: e.target.value })} className={inputCls + " flex-1 h-8"} />
                                                    <select data-testid={`check-grid-chkcol-type-${i}`} value={c.type} disabled={readonly}
                                                        onChange={(e) => updChkCol(i, { type: e.target.value, fault_values: [] })} className={inputCls + " h-8 w-40"}>
                                                        {CHECK_COLUMN_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                                                    </select>
                                                    {!readonly && <button onClick={() => rmChkCol(i)} className="p-1 rounded hover:bg-red-50 text-[#9A968F] hover:text-red-600"><X size={12} /></button>}
                                                </div>
                                                {c.type === "select" && (
                                                    <input data-testid={`check-grid-chkcol-options-${i}`} value={(c.options || []).join(", ")} disabled={readonly}
                                                        placeholder="Options, comma-separated"
                                                        onChange={(e) => updChkCol(i, { options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                                                        className={inputCls + " h-8"} />
                                                )}
                                                {c.type === "number" && (
                                                    <input data-testid={`check-grid-chkcol-unit-${i}`} value={c.unit || ""} disabled={readonly} placeholder="Unit (e.g. °C, mm) — optional"
                                                        onChange={(e) => updChkCol(i, { unit: e.target.value })} className={inputCls + " h-8"} />
                                                )}
                                                <div className="flex flex-wrap items-center gap-3 text-[11.5px] text-[#6F6A64]">
                                                    <label className="flex items-center gap-1.5"><input type="checkbox" checked={!!c.required} disabled={readonly} onChange={(e) => updChkCol(i, { required: e.target.checked })} />Required</label>
                                                    <label className="flex items-center gap-1.5"><input type="checkbox" checked={c.na_allowed !== false} disabled={readonly} onChange={(e) => updChkCol(i, { na_allowed: e.target.checked })} />Allow N/A</label>
                                                </div>
                                                {faultChoices.length > 0 ? (
                                                    <div className="flex flex-wrap items-center gap-2">
                                                        <span className="text-[11px] text-[#6F6A64]">Counts as a fault:</span>
                                                        {faultChoices.map((v) => (
                                                            <label key={v} className="flex items-center gap-1 text-[11.5px] px-2 py-0.5 rounded-full border border-[#E5E0DA]">
                                                                <input type="checkbox" checked={(c.fault_values || []).includes(v)} disabled={readonly}
                                                                    onChange={(e) => updChkCol(i, { fault_values: e.target.checked ? [...(c.fault_values || []), v] : (c.fault_values || []).filter((x) => x !== v) })} />
                                                                {v}
                                                            </label>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <input value={(c.fault_values || []).join(", ")} disabled={readonly} placeholder="Fault values, comma-separated (optional)"
                                                        onChange={(e) => updChkCol(i, { fault_values: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                                                        className={inputCls + " h-8"} />
                                                )}
                                            </div>
                                        );
                                    })}
                                    {chkCols.length === 0 && <p className="text-[11px] text-[#9A968F] italic">No check columns yet</p>}
                                </div>
                            </div>
                        </>
                    )}

                    {tab === "rows" && (
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <p className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64]">Rows</p>
                                {!readonly && (
                                    <div className="flex items-center gap-2">
                                        <button data-testid="check-grid-paste-rows-btn" onClick={() => setPasteOpen(true)}
                                            className="text-[11px] text-[var(--brand-accent,_#C75A1B)] font-semibold hover:underline flex items-center gap-0.5"><ClipboardPaste size={11} />Paste rows</button>
                                        <button data-testid="check-grid-add-row" onClick={addRow} className="text-[11px] text-[var(--brand-accent,_#C75A1B)] font-semibold hover:underline flex items-center gap-0.5"><Plus size={11} />Add row</button>
                                    </div>
                                )}
                            </div>
                            {idCols.length === 0 ? (
                                <p className="text-[11px] text-[#9A968F] italic">Add at least one reference column first.</p>
                            ) : (
                                <div className="overflow-x-auto border border-[#E5E0DA] rounded-lg">
                                    <table className="w-full text-[12px]">
                                        <thead>
                                            <tr className="bg-[#F6F4F0]">
                                                {idCols.map((c) => <th key={c.id} className="text-left px-2 py-1.5 font-semibold text-[#6F6A64]">{c.label || "—"}</th>)}
                                                {chkCols.length > 0 && <th className="text-left px-2 py-1.5 font-semibold text-[#6F6A64]">N/A columns</th>}
                                                <th className="text-left px-2 py-1.5 font-semibold text-[#6F6A64]">Condition</th>
                                                {!readonly && <th className="w-8" />}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {rows.map((r, i) => (
                                                <Fragment key={r.id}>
                                                <tr className="border-t border-[#F0EDE8]" data-testid={`check-grid-row-${i}`}>
                                                    {idCols.map((c) => (
                                                        <td key={c.id} className="px-2 py-1">
                                                            <input value={(r.id_values || {})[c.id] || ""} disabled={readonly}
                                                                onChange={(e) => updRow(i, { id_values: { ...(r.id_values || {}), [c.id]: e.target.value } })}
                                                                className={inputCls + " h-7"} />
                                                        </td>
                                                    ))}
                                                    {chkCols.length > 0 && (
                                                        <td className="px-2 py-1">
                                                            <div className="flex flex-wrap gap-1.5">
                                                                {chkCols.map((c) => (
                                                                    <label key={c.id} className="flex items-center gap-1 text-[10.5px] text-[#6F6A64]">
                                                                        <input type="checkbox" checked={(r.na_columns || []).includes(c.id)} disabled={readonly} onChange={() => toggleRowNa(i, c.id)} />
                                                                        {c.label}
                                                                    </label>
                                                                ))}
                                                            </div>
                                                        </td>
                                                    )}
                                                    <td className="px-2 py-1">
                                                        <button data-testid={`check-grid-row-condition-${i}`} type="button"
                                                            onClick={() => setShowIfRowId(showIfRowId === r.id ? null : r.id)}
                                                            className={`text-[10.5px] font-semibold hover:underline ${r.show_if ? "text-[var(--brand-accent,_#C75A1B)]" : "text-[#9A968F]"}`}>
                                                            {r.show_if ? "Conditional ●" : "Show only when…"}
                                                        </button>
                                                    </td>
                                                    {!readonly && (
                                                        <td className="px-2 py-1"><button onClick={() => rmRow(i)} className="p-1 rounded hover:bg-red-50 text-[#9A968F] hover:text-red-600"><X size={12} /></button></td>
                                                    )}
                                                </tr>
                                                {showIfRowId === r.id && (
                                                    <tr className="border-t border-[#F0EDE8] bg-[#FAF9F7]">
                                                        <td colSpan={idCols.length + (chkCols.length > 0 ? 1 : 0) + 1 + (readonly ? 0 : 1)} className="px-2 py-2">
                                                            <ShowIfEditor testPrefix={`check-grid-row-showif-${i}`} showIf={r.show_if}
                                                                onChange={(v) => updRow(i, { show_if: v })}
                                                                availableFields={allFields} readonly={readonly} />
                                                        </td>
                                                    </tr>
                                                )}
                                                </Fragment>
                                            ))}
                                        </tbody>
                                    </table>
                                    {rows.length === 0 && <p className="text-[11px] text-[#9A968F] italic p-3">No rows yet</p>}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="px-5 py-3 border-t border-[#E5E0DA] flex justify-end">
                    <button data-testid="check-grid-dialog-done" onClick={onClose} className="px-4 py-2 rounded-lg text-[12.5px] font-semibold text-white" style={{ background: "var(--brand-accent, #C75A1B)" }}>Done</button>
                </div>
            </div>

            {pasteOpen && (
                <div className="fixed inset-0 z-[70] bg-black/40 grid place-items-center p-4" data-testid="check-grid-paste-dialog">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
                        <div className="px-5 py-4 border-b border-[#E5E0DA] flex items-center justify-between">
                            <h3 className="text-base font-bold text-[#1F1F1F]">Paste rows</h3>
                            <button onClick={() => setPasteOpen(false)} className="p-1 text-[#9A968F] hover:text-[#1F1F1F]"><X size={16} /></button>
                        </div>
                        <div className="p-5 space-y-3 overflow-y-auto">
                            <p className="text-[11.5px] text-[#6F6A64]">Paste tab-separated reference data from Excel/Word — one row per line, columns in the order: {idCols.map((c) => c.label || "?").join(" | ")}. A header row matching the column labels is skipped automatically.</p>
                            <textarea data-testid="check-grid-paste-textarea" rows={8} value={pasteText} onChange={(e) => setPasteText(e.target.value)}
                                placeholder={idCols.map((c) => c.label || "Column").join("\t")} className={inputCls + " font-mono resize-none"} />
                            {pasteText.trim() && (
                                <div className="border border-[#E5E0DA] rounded-lg p-3 bg-[#FAF9F7]">
                                    <p className="text-[11.5px] font-semibold text-[#1F1F1F] mb-1.5">
                                        Preview — {parsedPaste.rows.length} row{parsedPaste.rows.length !== 1 ? "s" : ""} will be added
                                        {parsedPaste.skippedHeader ? " (header row skipped)" : ""}
                                        {parsedPaste.truncated > 0 ? ` · ${parsedPaste.truncated} row(s) had extra cells, ignored` : ""}
                                    </p>
                                    <div className="max-h-40 overflow-y-auto text-[11px]">
                                        {parsedPaste.rows.slice(0, 20).map((r, i) => (
                                            <div key={r.id} className="py-0.5 border-t border-[#F0EDE8] first:border-t-0">{idCols.map((c) => r.id_values[c.id]).join(" | ")}</div>
                                        ))}
                                        {parsedPaste.rows.length > 20 && <p className="text-[#9A968F] italic pt-1">…and {parsedPaste.rows.length - 20} more</p>}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="px-5 py-3 border-t border-[#E5E0DA] flex justify-end gap-2">
                            <button onClick={() => setPasteOpen(false)} className="px-3 py-2 text-[12.5px] font-semibold rounded-lg border border-[#E5E0DA] text-[#6F6A64]">Cancel</button>
                            <button data-testid="check-grid-paste-confirm" onClick={confirmPaste} disabled={!parsedPaste.rows.length}
                                className="px-4 py-2 text-[12.5px] font-semibold rounded-lg text-white disabled:opacity-50" style={{ background: "var(--brand-accent, #C75A1B)" }}>
                                Add {parsedPaste.rows.length} row{parsedPaste.rows.length !== 1 ? "s" : ""}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function Group({ label, helper, children }) {
    return (
        <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] mb-1">{label}</label>
            {children}
            {helper && <p className="text-[11px] text-[#9A968F] mt-1">{helper}</p>}
        </div>
    );
}

const SHOWIF_OPS = [
    { value: "eq", label: "equals" },
    { value: "neq", label: "does not equal" },
    { value: "not_empty", label: "is answered" },
    { value: "empty", label: "is not answered" },
];

// Author-time editor for a {all: [{field_id, op, value}]} show_if rule — used on
// sections, fields and (via a prop) check-grid rows. One condition for simplicity.
function ShowIfEditor({ showIf, onChange, availableFields, readonly, testPrefix }) {
    const enabled = !!showIf;
    const cond = (showIf?.all || [])[0] || { field_id: "", op: "eq", value: "" };
    const refField = availableFields.find((f) => f.id === cond.field_id);

    const update = (patch) => onChange({ all: [{ ...cond, ...patch }] });
    const toggle = (on) => onChange(on ? { all: [{ field_id: availableFields[0]?.id || "", op: "eq", value: "" }] } : null);

    const valueControl = () => {
        if (cond.op === "not_empty" || cond.op === "empty") return null;
        if (!refField) return <input disabled className={inputCls + " h-8"} placeholder="Pick a field first" />;
        if (refField.field_type === "yes_no") {
            return (
                <select data-testid={`${testPrefix}-value`} disabled={readonly} value={cond.value || ""}
                    onChange={(e) => update({ value: e.target.value })} className={inputCls + " h-8"}>
                    <option value="">— value —</option>
                    <option value="yes">Yes</option><option value="no">No</option><option value="na">N/A</option>
                </select>
            );
        }
        if (["dropdown", "multi_select"].includes(refField.field_type)) {
            return (
                <select data-testid={`${testPrefix}-value`} disabled={readonly} value={cond.value || ""}
                    onChange={(e) => update({ value: e.target.value })} className={inputCls + " h-8"}>
                    <option value="">— value —</option>
                    {(refField.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
            );
        }
        if (refField.field_type === "checkbox") {
            return (
                <select data-testid={`${testPrefix}-value`} disabled={readonly} value={String(cond.value ?? "")}
                    onChange={(e) => update({ value: e.target.value === "true" })} className={inputCls + " h-8"}>
                    <option value="">— value —</option>
                    <option value="true">Checked</option><option value="false">Unchecked</option>
                </select>
            );
        }
        return (
            <input data-testid={`${testPrefix}-value`} disabled={readonly} value={cond.value ?? ""}
                onChange={(e) => update({ value: e.target.value })} className={inputCls + " h-8"} placeholder="Value" />
        );
    };

    return (
        <div className="border border-[#E5E0DA] rounded-lg p-2.5 bg-[#FAF9F7] space-y-1.5">
            <label className="flex items-center gap-2 text-[11.5px] font-semibold text-[#1F1F1F]">
                <input type="checkbox" data-testid={`${testPrefix}-enable`} checked={enabled} disabled={readonly}
                    onChange={(e) => toggle(e.target.checked)} />
                Show only when…
            </label>
            {enabled && (
                <div className="flex items-center gap-1.5 flex-wrap pl-5">
                    <select data-testid={`${testPrefix}-field`} disabled={readonly} value={cond.field_id || ""}
                        onChange={(e) => update({ field_id: e.target.value })} className={inputCls + " h-8 w-40"}>
                        <option value="">Select field…</option>
                        {availableFields.map((f) => <option key={f.id} value={f.id}>{f.label || "Untitled"}</option>)}
                    </select>
                    <select data-testid={`${testPrefix}-op`} disabled={readonly} value={cond.op || "eq"}
                        onChange={(e) => update({ op: e.target.value })} className={inputCls + " h-8 w-32"}>
                        {SHOWIF_OPS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    {valueControl()}
                </div>
            )}
        </div>
    );
}

function MetaDialog({ meta, setMeta, onClose, onSave, isInternal }) {
    return (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
                <div className="px-5 py-4 border-b border-[#E5E0DA] flex items-center justify-between">
                    <h3 className="text-base font-bold text-[#1F1F1F]">Template settings</h3>
                    <button onClick={onClose} className="p-1 text-[#9A968F] hover:text-[#1F1F1F]"><X size={16} /></button>
                </div>
                <div className="p-5 space-y-3">
                    <Group label="Name *">
                        <input value={meta.name} onChange={(e) => setMeta({ ...meta, name: e.target.value })} className={inputCls} />
                    </Group>
                    <Group label="Category">
                        <select value={meta.category} onChange={(e) => setMeta({ ...meta, category: e.target.value })} className={inputCls}>
                            {["inspection", "audit", "fra", "bsc", "cdm"].map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </Group>
                    <Group label="Description">
                        <textarea rows={3} value={meta.description} onChange={(e) => setMeta({ ...meta, description: e.target.value })} className={inputCls + " resize-none"} />
                    </Group>
                    {isInternal && (
                        <Group label="Weekly Site Report category" helper="Controls which weekly-report log (Quality / Health &amp; Safety) this template's completed instances appear in.">
                            <select data-testid="builder-report-category-select" value={meta.report_category || ""}
                                onChange={(e) => setMeta({ ...meta, report_category: e.target.value })} className={inputCls}>
                                <option value="">Auto (by name)</option>
                                <option value="quality">Quality</option>
                                <option value="hs">Health &amp; Safety</option>
                                <option value="none">Not in weekly report</option>
                            </select>
                        </Group>
                    )}
                    {isInternal && (
                        <Group label="Who is allowed to run this form" helper="Not the same as the report's owner — that's set per-report on the completed instance.">
                            <select data-testid="builder-completed-by-select" value={meta.completed_by || "pr"}
                                onChange={(e) => setMeta({ ...meta, completed_by: e.target.value })} className={inputCls}>
                                <option value="pr">PR staff (completed on a visit)</option>
                                <option value="client">Client (self-service "Start new report")</option>
                            </select>
                        </Group>
                    )}
                    {isInternal && (
                        <label className="flex items-start gap-2 text-sm">
                            <input data-testid="builder-pr-approval-checkbox" type="checkbox" className="mt-0.5" checked={!!meta.pr_approval_required}
                                onChange={(e) => setMeta({ ...meta, pr_approval_required: e.target.checked })} />
                            <span>
                                Requires Priority Risk approval / signatory
                                <span className="block text-[11px] text-[#9A968F] mt-0.5">When on, Priority Risk must approve this report after it is submitted — even when the client completes it.</span>
                            </span>
                        </label>
                    )}
                    <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={meta.requires_signature}
                            onChange={(e) => setMeta({ ...meta, requires_signature: e.target.checked })} />
                        Require signature on submit
                    </label>
                </div>
                <div className="px-5 py-3 border-t border-[#E5E0DA] flex items-center justify-end gap-2 bg-[#F5F3F0]">
                    <button data-testid="builder-meta-cancel" onClick={onClose} className="px-3 py-2 text-sm text-[#6F6A64] hover:text-[#1F1F1F]">Cancel</button>
                    <button data-testid="builder-meta-save" onClick={onSave}
                        className="inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-lg text-white"
                        style={{ background: "var(--brand-accent, #C75A1B)" }}>
                        <Save size={14} /> Save
                    </button>
                </div>
            </div>
        </div>
    );
}

const inputCls = "w-full h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm focus:outline-none focus:border-[var(--brand-accent,_#C75A1B)] disabled:bg-[#F6F4F0]";
